import {
  Boxes,
  LayoutDashboard,
  LogOut,
  MapPinned,
  RadioTower,
  ShieldCheck,
} from 'lucide-react'
import { lazy, Suspense } from 'react'
import { Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom'
import { useAuth } from './auth-context'
import { Button } from '../components/ui'

const LoginPage = lazy(() => import('../pages/LoginPage').then(module => ({ default: module.LoginPage })))
const OverviewPage = lazy(() => import('../pages/OverviewPage').then(module => ({ default: module.OverviewPage })))
const OperationsPage = lazy(() => import('../pages/OperationsPage').then(module => ({ default: module.OperationsPage })))
const AccessPage = lazy(() => import('../pages/AccessPage').then(module => ({ default: module.AccessPage })))

const navItems = [
  { to: '/overview', label: '态势', icon: LayoutDashboard },
  { to: '/operations', label: '运营', icon: Boxes },
  { to: '/access', label: '权限', icon: ShieldCheck },
]

function LoadingScreen() {
  return (
    <div className="boot-screen">
      <div className="brand-sigil"><MapPinned /></div>
      <p className="eyebrow">FIELD CONSOLE</p>
      <h1>正在校验运营身份</h1>
      <div className="boot-line"><span /></div>
    </div>
  )
}

function ForbiddenScreen() {
  const { logout, error } = useAuth()
  return (
    <div className="access-denied">
      <div className="access-denied__mark"><ShieldCheck /></div>
      <p className="eyebrow">ACCESS RESTRICTED</p>
      <h1>账号尚未获得后台席位</h1>
      <p>{error || '请联系超级管理员授予后台角色，登录成功本身不会自动获得业务权限。'}</p>
      <Button onClick={() => void logout()}>退出当前账号</Button>
    </div>
  )
}

function ProtectedLayout() {
  const { status, me, logout } = useAuth()
  const location = useLocation()
  if (status === 'loading') return <LoadingScreen />
  if (status === 'guest') return <Navigate to="/login" replace state={{ from: location.pathname }} />
  if (status === 'forbidden') return <ForbiddenScreen />
  if (!me) return <Navigate to="/login" replace />

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand-lockup">
          <div className="brand-sigil"><MapPinned /></div>
          <div><strong>寻宝记</strong><span>FIELD OPS / 09</span></div>
        </div>
        <div className="environment-stamp"><RadioTower size={14} /><span>生产环境</span><i /></div>
        <nav>
          {navItems.map(({ to, label, icon: Icon }, index) => (
            <NavLink key={to} to={to}>
              <span className="nav-index">0{index + 1}</span><Icon size={18} /><span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar__account">
          <div className="account-avatar">{me.displayName.slice(0, 1)}</div>
          <div><strong>{me.displayName}</strong><span>{me.roles.length} 个有效角色</span></div>
          <button type="button" aria-label="退出登录" onClick={() => void logout()}><LogOut size={17} /></button>
        </div>
      </aside>
      <main className="main-stage">
        <div className="stage-ruler"><span>30°16′N</span><i /><span>120°09′E</span><i /><span>UTC+08</span></div>
        <Routes>
          <Route path="/overview" element={<OverviewPage />} />
          <Route path="/operations" element={<OperationsPage />} />
          <Route path="/access" element={<AccessPage />} />
          <Route path="*" element={<Navigate to="/overview" replace />} />
        </Routes>
      </main>
      <nav className="mobile-nav">
        {navItems.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to}><Icon size={20} /><span>{label}</span></NavLink>
        ))}
      </nav>
    </div>
  )
}

export function App() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/*" element={<ProtectedLayout />} />
      </Routes>
    </Suspense>
  )
}
